import React from 'react';
import { Dialog, Grid, Typography } from '@mui/material';

import { useTheme } from '@emotion/react';

import { Pendingwarning } from 'component/BuyersMarketplace/MakeOffers';
import { poppins400, poppins500 } from 'theme/common.style';
import { Button, useMediaQuery } from '@mui/material';

const PendingCounterPrivatePopup = ({
  open,
  onClose,
  onContinue,
  address,
  manageOfferData,
  onContinues,
  onCloses,
  tableData,
  pendingOfferType,
  fromTableHead = false,
  previousOfferAmount,
  currentOfferAmount,
  previousOfferType,
  scenario,
}) => {
  const theme = useTheme();
  const matchessm = useMediaQuery(theme.breakpoints.down('sm'));
  const matchesmd = useMediaQuery(theme.breakpoints.down('md'));

  const isMultipleTypes =
    Array.isArray(pendingOfferType) && pendingOfferType.length > 1;
  const hasCounterPending = isMultipleTypes
    ? pendingOfferType.includes('Counter Pending')
    : pendingOfferType === 'Counter Pending';
  const hasPrivatePending = isMultipleTypes
    ? pendingOfferType.includes('Private Pending')
    : pendingOfferType === 'Private Pending';

  const getTitleText = () => {
    if (fromTableHead && pendingOfferType?.length > 0) {
      return 'Pending offers detected';
    }

    if (previousOfferType === 'Counter Pending') {
      if (scenario === 'counter-to-counter') {
        return 'Counter offer already exists';
      }
      return 'Counter offer already exists';
    }

    if (previousOfferType === 'Private Pending') {
      if (scenario === 'private-to-lower-counter') {
        return 'Higher Private Detected';
      }

      if (scenario === 'private-to-higher-counter') {
        return 'Lower Private Detected';
      }

      if (scenario === 'private-to-same-counter') {
        return 'Same Private Detected';
      }

      return 'Private offer already exists';
    }

    return '';
  };

  const getDescriptionText = () => {
    const buyerText = fromTableHead ? 'selected buyers' : 'this buyer';

    if (fromTableHead && pendingOfferType?.length > 0) {
      return (
        <>
          Some buyers have pending{' '}
          {pendingOfferType.includes('Counter Pending') &&
          pendingOfferType.includes('Private Pending')
            ? 'counter offers and private offers'
            : pendingOfferType.includes('Counter Pending')
            ? 'counter offers'
            : 'private offers'}
          . You are now forwarding a new counter offer of{' '}
          <strong>{currentOfferAmount}</strong>. This may override the existing
          offers and affect the current offer position.
        </>
      );
    }

    if (!previousOfferType) return '';

    if (
      previousOfferType === 'Counter Pending' &&
      scenario === 'counter-to-counter'
    ) {
      return (
        <>
          You have already forwarded a counter offer to the buyers. You are now
          forwarding another counter offer of{' '}
          <strong>{currentOfferAmount}</strong>. Forwarding this offer may
          override the previous counter offer.
        </>
      );
    }

    if (
      previousOfferType === 'Private Pending' &&
      scenario === 'private-to-lower-counter'
    ) {
      return (
        <>
          You have already shared a private offer of{' '}
          <strong>{previousOfferAmount}</strong> with {buyerText}. You are now
          forwarding a lower counter offer of{' '}
          <strong>{currentOfferAmount}</strong>.
        </>
      );
    }

    if (
      previousOfferType === 'Private Pending' &&
      scenario === 'private-to-higher-counter'
    ) {
      return (
        <>
          You have previously forwarded a private offer of{' '}
          <strong>{previousOfferAmount}</strong> to other buyers. You are now
          forwarding a higher counter offer of{' '}
          <strong>{currentOfferAmount}</strong>. This may create confusion for
          buyers and override the current offer position.
        </>
      );
    }

    if (
      previousOfferType === 'Private Pending' &&
      scenario === 'private-to-same-counter'
    ) {
      return (
        <>
          You have already shared a private offer of{' '}
          <strong>{previousOfferAmount}</strong> with {buyerText}. You are
          forwarding a counter offer with the same amount of{' '}
          <strong>{currentOfferAmount}</strong>. This may override the current
          offer position.
        </>
      );
    }

    if (previousOfferType === 'Counter Pending') {
      return `A pending counter offer already exists for ${buyerText}. Sending a new counter offer will automatically withdraw the existing one to ensure only one active offer remains.`;
    }

    if (previousOfferType === 'Private Pending') {
      return `A pending private offer already exists for ${buyerText}. Sending a new private offer will automatically withdraw the existing one to ensure only one active offer remains.`;
    }

    return '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '5px',
          minWidth: matchessm ? '95%' : '450px',
          maxWidth: '500px',
          boxShadow: ' -4px, 4px rgba(0, 0, 0, 0.09)',
          padding: matchessm ? '30px 20px' : '30px',
        },
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          display={'flex'}
          alignItems={'center'}
          sx={{ mb: 1 }}
        >
          <Pendingwarning />
          <Typography
            sx={{
              ...poppins500,
              fontSize: matchessm ? '18px' : '22px',
              paddingLeft: '12px',
            }}
          >
            {getTitleText()}
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ mb: 3 }}>
          <Typography
            sx={{
              ...poppins400,
              fontSize: matchesmd ? '14px' : '15px',
              color: '#666666',
              whiteSpace: 'pre-line',
              lineHeight: 1.6,
            }}
          >
            {getDescriptionText()}
          </Typography>
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent={'flex-end'}
          gap={2}
        >
          <Grid item>
            <Button
              variant="outlined"
              sx={{
                padding: matchessm ? '6px 25px' : '8px 35px',
                height: matchessm ? '36px' : '42px',
                fontSize: matchessm ? '13px' : '14px',
                color: '#666666',
                fontWeight: '400',
                fontFamily: 'Poppins',
                border: '1px solid rgba(17, 35, 71, 0.1)',
                borderRadius: '5px',
                textTransform: 'capitalize',
                '&:hover': {
                  border: '1px solid rgba(17, 35, 71, 0.2)',
                  backgroundColor: 'transparent',
                },
              }}
              onClick={onClose || onCloses}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              sx={{
                padding: matchessm ? '6px 25px' : '8px 35px',
                height: matchessm ? '36px' : '42px',
                fontSize: matchessm ? '13px' : '14px',
                color: '#FFFFFF',
                fontWeight: '400',
                fontFamily: 'Poppins',
                background: '#4CA4D6',
                borderRadius: '5px',
                textTransform: 'capitalize',
                '&:hover': {
                  background: '#3A8AB8',
                },
              }}
              onClick={onContinue || onClose}
            >
              Forward Offer
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default PendingCounterPrivatePopup;
